import { MSFP_LICENSE_PROPERTY_OBJECTS } from '@/constants/licenses'
import { getSportCompetitions } from '@/service/sportsService'
import { Competition } from '@/types/dde/season'
import { FormattedOperatorWithLicense } from '@/types/operator'
import logger from '@/utils/_legacy/logger'
import { notifyError } from '@/utils/notifyError'

export const searchLicenseProperties = (licenseProperty: string, searchTerm: string) =>
  licenseProperty.toLowerCase().includes(searchTerm.toLowerCase())

/**
 * Maps through the MSFP_LICENSE_PROPERTY_OBJECTS and returns the unique sports in an array.
 * @returns string[]
 */
export const getMSFPSportsByLicense = (): string[] => {
  return MSFP_LICENSE_PROPERTY_OBJECTS.reduce((acc: string[], curr) => {
    return curr.sport.length && !acc.includes(curr.sport) ? [...acc, curr.sport] : acc
  }, [])
}

/**
 * Calls every MSFP Competition end point and if data returned it will be stored in an object indexed by sport.
 * @returns { [sport: string]: Competition[]}
 */
export const getMSFPCompetitionData = async (): Promise<{ [sport: string]: Competition[] }> => {
  try {
    const sports = getMSFPSportsByLicense()

    const results: { [sport: string]: Competition[] } = sports.reduce(
      (acc, sport) => {
        acc[sport] = []
        return acc
      },
      {} as { [sport: string]: Competition[] }
    )

    for (const sport of sports) {
      try {
        const competitionData = await getSportCompetitions(sport.toLowerCase())

        if (competitionData && Array.isArray(competitionData.data)) {
          results[sport] = competitionData.data
        }
      } catch (error) {
        logger.error(error, `Error fetching competitions for sport ${sport}`)
        notifyError(`Error fetching competitions for sport ${sport}`)
      }
    }

    return results
  } catch (error) {
    logger.error(error, 'Error in getMSFPCompetitionData')
    return {}
  }
}

/**
 * Given an MSFP license property, this function will filter through all MSFP_LICENSE_PROPERTY_OBJECTS, find the matching data
 * and return the relevant sport for that property.
 * @params licenseProperty: string;
 * @returns string[]
 */
export const getMSFPLicensePropertySport = (licenseProperty: string): string | null => {
  const getMSFPLicenseProperty = MSFP_LICENSE_PROPERTY_OBJECTS.find(
    (MSFPLicense) => licenseProperty === MSFPLicense.value
  )
  return getMSFPLicenseProperty ? getMSFPLicenseProperty.sport : null
}

/**
 * Given a licenses competitionIds and the relevant sport's competitions, this function will find the matching competition data from the competitionId
 * and return the name for that competitionId. If no competition match then it will return the original competitionId.
 * @param licenseCompetitionIds
 * @param competitionData
 * @returns competitionName[]: string[]
 */
export const convertCompetitionIdsToCompetitionNames = (
  licenseCompetitionIds: string[],
  competitionData: Competition[]
): string[] => {
  const competitionNames = licenseCompetitionIds.map((id) => {
    const match = competitionData.find((item) => item.id === id)
    return match ? match.name : id
  })

  return competitionNames
}

/**
 * Given a list of OperatorsWithLicenses, this function will fetch all MSFP sports competition data and then replace the competitionIds
 * in all MSFP License objects with the names of the relevant competitions, whilst still retaining the original data structure.
 * @param operatorsWithLicense
 * @returns FormattedOperatorWithLicense[]
 */
export const getOperatorLicensesWithCompetitionNames = async (
  operatorsWithLicense: FormattedOperatorWithLicense[]
): Promise<FormattedOperatorWithLicense[]> => {
  try {
    const MSFPCompetitionData = await getMSFPCompetitionData()

    if (!MSFPCompetitionData || Object.keys(MSFPCompetitionData).length === 0) {
      return operatorsWithLicense
    }

    const operatorsWithPopulatedLicenses = operatorsWithLicense.reduce(
      (acc: FormattedOperatorWithLicense[], curr: FormattedOperatorWithLicense) => {
        if (!curr || !curr.license || !Array.isArray(curr.license)) {
          return acc
        }

        const populatedLicenses = curr.license.map((license) => {
          if (!license || !license.property) {
            return license
          }

          const currentLicensePropery = license.property.toString()
          const currentLicenseSport = getMSFPLicensePropertySport(currentLicensePropery)

          if (!currentLicenseSport || !MSFPCompetitionData[currentLicenseSport]) return license

          const populatedLicensingPeriods = (license.licensingPeriods || []).map(
            (licensePeriod) => {
              if (
                licensePeriod?.subscription?.competitionIds &&
                Array.isArray(licensePeriod.subscription.competitionIds) &&
                licensePeriod.subscription.competitionIds.length > 0
              ) {
                try {
                  const populatedCompetitionIds = convertCompetitionIdsToCompetitionNames(
                    licensePeriod.subscription.competitionIds,
                    MSFPCompetitionData[currentLicenseSport]
                  )
                  return {
                    ...licensePeriod,
                    subscription: {
                      ...licensePeriod.subscription,
                      competitionIds: populatedCompetitionIds
                    }
                  }
                } catch (error) {
                  return licensePeriod
                }
              }
              return licensePeriod
            }
          )

          return { ...license, licensingPeriods: populatedLicensingPeriods }
        })

        return [...acc, { ...curr, license: populatedLicenses }]
      },
      []
    )

    return operatorsWithPopulatedLicenses
  } catch (error) {
    logger.error(error, 'Error in getOperatorLicensesWithCompetitionNames')
    return operatorsWithLicense
  }
}
